// © 2026 Holmes Computer Consultants. All rights reserved.
import { BufferAttribute, BufferGeometry, Mesh, DataTexture, RepeatWrapping, LinearFilter,
  LinearMipmapLinearFilter, SRGBColorSpace, type Material, type Group } from 'three'
import type asset from '../../SourceArt/PierCabinets/mesh.json'
import { bodyLengthFor, cabinetBottomY, type CabinetDimensions } from './cabinet'
import { FINISHES } from './partsKit'
import { MeshStandardMaterial } from 'three'
import { prepareCabinetLegs } from './cabinetLegs'
import {prepareCabinetTrim} from './cabinetTrim'

let data: typeof asset | undefined
export async function prepareAuthoredCabinets(): Promise<void> {
  // Publish the leg-free rail/door kit only when replacement supports are ready too.
  const [asset] = await Promise.all([import('../../SourceArt/PierCabinets/mesh.json'),prepareCabinetLegs(),prepareCabinetTrim()])
  data ??= asset.default
}
export function authoredCabinetFor(machineId: string | undefined, dims: CabinetDimensions) {
  const fitted=data?.cabinets.find(c=>machineId!==undefined&&c.machineIds.includes(machineId))
  if(!fitted || Math.abs(fitted.width-dims.width)>1e-8 || Math.abs(fitted.length-bodyLengthFor(dims))>1e-8
    ||Math.abs(fitted.incline-dims.incline)>1e-8 ||Math.abs(fitted.frontTopY-dims.lockdownBarHeight)>1e-8
    ||Math.abs(fitted.bottomY-cabinetBottomY(dims))>1e-8)return undefined
  return fitted
}
export function addAuthoredCabinet(fitted: NonNullable<ReturnType<typeof authoredCabinetFor>>, group: Group,
  geometries: BufferGeometry[], materials: Material[]): void {
  for(const part of fitted.parts){
    const geometry=new BufferGeometry()
    geometry.setAttribute('position',new BufferAttribute(new Float32Array(part.positions.map(v=>v*part.positionsScale)),3))
    geometry.setAttribute('normal',new BufferAttribute(new Float32Array(part.normals.map(v=>v*part.normalsScale)),3))
    geometry.setAttribute('uv',new BufferAttribute(new Float32Array(part.uvs.map(v=>v*part.uvsScale)),2))
    geometry.setIndex(part.triangles);geometry.normalizeNormals();geometry.computeBoundingBox();geometry.computeBoundingSphere();geometries.push(geometry)
    const finish=part.finish==='HardwareSteel'?FINISHES.hardwareSteel:FINISHES.hardwareRubber
    const material=part.finish==='HardwareSteel'?brushedMetal():new MeshStandardMaterial(finish);materials.push(material)
    const mesh=new Mesh(geometry,material);mesh.name=`cabinet.authored.${part.finish}`;group.add(mesh)
  }
}

/** Only this new cabinet assembly uses these two small, mipmapped surface maps. */
function brushedMetal(): MeshStandardMaterial {
  const size=128, albedo=new Uint8Array(size*size*4), rough=new Uint8Array(size*size*4)
  for(let y=0;y<size;y++)for(let x=0;x<size;x++){
    const grain=((x*73+19)%127)/126, fleck=((x*17+y*29)%31)/30, at=(y*size+x)*4
    const value=Math.round(224+grain*24+fleck*4), r=Math.round((.24+grain*.10)*255)
    albedo.set([value,value,value,255],at);rough.set([0,r,0,255],at)
  }
  const map=new DataTexture(albedo,size,size), roughnessMap=new DataTexture(rough,size,size)
  map.colorSpace=SRGBColorSpace
  for(const texture of [map,roughnessMap]){
    texture.wrapS=texture.wrapT=RepeatWrapping;texture.generateMipmaps=true
    texture.minFilter=LinearMipmapLinearFilter;texture.magFilter=LinearFilter;texture.needsUpdate=true
  }
  const material=new MeshStandardMaterial({color:0xcdd1d6,metalness:1,roughness:1,envMapIntensity:1.1,map,roughnessMap})
  material.name='cabinet.brushedSteel'
  material.addEventListener('dispose',()=>{map.dispose();roughnessMap.dispose()})
  return material
}
